import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { clientService } from '../services/clientService';
import { assessmentService } from '../services/assessmentService';
import { XMarkIcon, UserPlusIcon, UserIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

interface InviteUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    assessmentId: number;
    clientId: number;
    clientName: string;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
    isOpen,
    onClose,
    assessmentId,
    clientId,
    clientName
}) => {
    const [inviteType, setInviteType] = useState<'existing' | 'new'>('existing');
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [newUserData, setNewUserData] = useState({
        email: '',
        firstName: '',
        lastName: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: clientUsers, isLoading: isLoadingUsers } = useQuery(
        ['clientUsers', clientId],
        () => clientService.getClientUsers(clientId),
        { enabled: isOpen && clientId > 0 }
    );

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (inviteType === 'existing') {
                if (!selectedUserId) {
                    toast.error('Please select a user');
                    setIsSubmitting(false);
                    return;
                }
                await assessmentService.inviteUser(assessmentId, { userId: parseInt(selectedUserId) });
            } else {
                if (!newUserData.email || !newUserData.firstName || !newUserData.lastName) {
                    toast.error('Please fill in all fields');
                    setIsSubmitting(false);
                    return;
                }
                await assessmentService.inviteUser(assessmentId, newUserData);
            }

            toast.success('Invitation sent successfully');
            onClose();
        } catch (error: any) {
            toast.error(error.message || 'Failed to send invitation');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <UserPlusIcon className="w-5 h-5 mr-2 text-primary-600" />
                        Invite to Evaluation
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleInvite}>
                    <div className="p-6 space-y-5">
                        <div className="text-sm text-gray-600 mb-2">
                            Invite a user from <span className="font-semibold text-gray-900">{clientName}</span> to collaborate on this evaluation.
                        </div>

                        {/* Copy Link Section */}
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Evaluation Link</label>
                            <div className="flex items-center space-x-2 bg-white rounded border border-gray-200 px-2 py-1.5 transition-shadow hover:shadow-sm">
                                <input
                                    readOnly
                                    value={window.location.href}
                                    className="block w-full text-sm text-gray-600 border-none p-0 focus:ring-0"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard.writeText(window.location.href);
                                        toast.success('Link copied!');
                                    }}
                                    className="text-primary-600 hover:text-primary-700 font-medium text-xs whitespace-nowrap bg-primary-50 px-2 py-1 rounded hover:bg-primary-100 transition-colors"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>

                        {/* Toggle Switch */}
                        <div className="flex p-1 bg-gray-100 rounded-lg">
                            <button
                                type="button"
                                onClick={() => setInviteType('existing')}
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${inviteType === 'existing'
                                    ? 'bg-white text-primary-600 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Existing User
                            </button>
                            <button
                                type="button"
                                onClick={() => setInviteType('new')}
                                className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all ${inviteType === 'new'
                                    ? 'bg-white text-primary-600 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                New User
                            </button>
                        </div>

                        {inviteType === 'existing' ? (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Select Existing User</label>
                                {isLoadingUsers ? (
                                    <div className="flex justify-center py-4">
                                        <LoadingSpinner size="sm" />
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <UserIcon className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <select
                                            value={selectedUserId}
                                            onChange={(e) => setSelectedUserId(e.target.value)}
                                            className="block w-full pl-10 pr-3 py-2 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md appearance-none bg-white"
                                        >
                                            <option value="">Choose a user...</option>
                                            {clientUsers?.map((u: any) => (
                                                <option key={u.id} value={u.id}>
                                                    {u.firstName} {u.lastName} ({u.email})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {clientUsers && clientUsers.length === 0 && !isLoadingUsers && (
                                    <p className="text-xs text-amber-600">No existing users found for this client.</p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700">First Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={newUserData.firstName}
                                            onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                                            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                            placeholder="John"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700">Last Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={newUserData.lastName}
                                            onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                                            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                            placeholder="Doe"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-700">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        value={newUserData.email}
                                        onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="john.doe@example.com"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all shadow-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <LoadingSpinner size="sm" className="mr-2 border-white" />
                                    Inviting...
                                </>
                            ) : (
                                'Send Invitation'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InviteUserModal;
